import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { decideStaffAccess } from '@/lib/staffAccess';
import { PatientStaffBlocked } from '@/pages/auth/PatientStaffBlocked';

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-ds-bg">
      <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-ds-teal" />
    </div>
  );
}

/**
 * Gates clinic-staff routes behind an authenticated session with a staff role.
 * Patient-only accounts (no active clinic_membership / platform admin) see a
 * dedicated page instead of the staff shell — no sidebar, no staff queries.
 * Patient portal / public booking get their own gates in MOD-3 / MOD-13.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { status, memberships, membershipsResolved, isPlatformAdmin } = useAuth();
  const location = useLocation();

  if (status === 'initializing') {
    return <FullPageSpinner />;
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  const decision = decideStaffAccess({
    isPlatformAdmin,
    membershipCount: memberships.length,
    membershipsResolved,
  });

  switch (decision) {
    case 'pending':
      return <FullPageSpinner />;
    case 'patient_only':
      return <PatientStaffBlocked />;
    case 'staff':
      return <>{children}</>;
    default: {
      const _exhaustive: never = decision;
      return _exhaustive;
    }
  }
}
